import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { type AutoAddressMode, parseAutoAddressMode } from '@/lib/content-doctrine/article-address';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

/**
 * The per-workspace setting for automatic addressing of review notes (`brand_kits.editorial_auto_address`).
 *
 *   off    (the default for a workspace that has not chosen) the operator runs "Address review notes" from the
 *          critique panel; nothing starts by itself
 *   prose  after a check, the system addresses only prose, structure and voice notes that are editorial judgment
 *   all    after a check or a claim check, the system addresses every gate-relevant note and wrong-law claim
 *
 * Each is ONE pass per version and at most two in a row, then the new version is re-checked (see
 * `workflows/steps/article/chain-core.ts`).
 *
 * `brand_kits` is account-scoped (`table-scope.generated.ts`): the write filters on the account owner's id, not
 * the session user's, through `resolveOwnerId()`.
 */
const KEY = (workspaceId: string | null) => ['editorial-auto-address', workspaceId] as const;

export function useEditorialAutoAddress(workspaceId: string | null) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: KEY(workspaceId),
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<AutoAddressMode> => {
      const { data, error } = await supabase
        .from('brand_kits')
        .select('editorial_auto_address')
        .eq('id', workspaceId as string)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return parseAutoAddressMode(data?.editorial_auto_address);
    },
  });
  const update = useMutation({
    mutationFn: async (mode: AutoAddressMode): Promise<AutoAddressMode> => {
      if (!workspaceId) throw new Error('Select the article workspace first.');
      const ownerId = await resolveOwnerId();
      const { data, error } = await supabase
        .from('brand_kits')
        .update({ editorial_auto_address: mode })
        .eq('id', workspaceId)
        .eq('user_id', ownerId)
        .select('editorial_auto_address');
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error('This setting could not be saved.');
      return mode;
    },
    onSuccess: (mode) => {
      client.setQueryData(KEY(workspaceId), mode);
    },
  });
  return {
    mode: query.data ?? ('off' as AutoAddressMode),
    isPending: query.isPending,
    update,
  };
}
